import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridFilterModel,
  GridPinnedColumnFields,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { useState } from "react";
import { DataGridProps } from "@components/common/data-grid/adm-data-grid-types";
import {
  admDataGridSlots,
  AdmDataGridSlotPropsProvider,
} from "@components/common/data-grid/adm-data-grid-slots";
import { getDefaultFilterModel } from "@components/common/data-grid/toolbar/toolbar-helper";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { GeneralTableEvents } from "@utils/track-event/track-event-list";

/** A wrapper component around MUI DataGrid */
export function AdmDataGrid<T>({
  entities,
  uniqueIdKey,
  columns,
  onRowClick,
  isRowClickable,
  onExport,
  shouldShowErrorMessage,
  errorMessage,
  defaultSortModel,
  shouldForceSort = false,
  rowDetails,
  additionalToolbarButtons,
  leadingToolbarButtons,
  filterGroups,
  tableType,
  shouldShowVerticalGridLines = false,
  shouldHighlightHoveredColumn = false,
  shouldAllowColumnResize = false,
  pinnedLeftColumns,
  columnGroupingModel,
  selectedRowIds,
  onSelectedRowIdsChange,
}: DataGridProps<T>): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [activeFilters, setActiveFilters] = useState<
    GridFilterModel | undefined
  >(() => getDefaultFilterModel(filterGroups ?? []));

  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(
    new Set()
  );

  const rows = entities.map((entity) => ({
    id: entity[uniqueIdKey],
    entity,
  }));
  const firstColumnField = columns[0]?.field;
  const isRowSelectionEnabled = !!selectedRowIds && !!onSelectedRowIdsChange;
  const hoveredColumnStyles = shouldHighlightHoveredColumn
    ? Object.fromEntries(
        columns.flatMap((column) => {
          const hoverSelector = `&:has(.MuiDataGrid-cell[data-field='${column.field}']:hover)`;
          return [
            [
              `${hoverSelector} .MuiDataGrid-cell[data-field='${column.field}']`,
              { backgroundColor: "action.hover" },
            ],
            [
              `${hoverSelector} .MuiDataGrid-columnHeader[data-field='${column.field}']`,
              { backgroundColor: "action.hover" },
            ],
          ];
        })
      )
    : {};

  /** Toggles the detail panel for a given row open or closed. Only one row can be expanded at a time. */
  function toggleRowExpansion(id: GridRowId): void {
    setExpandedRowIds((prev) => {
      if (prev.has(id)) {
        return new Set();
      }
      return new Set([id]);
    });
  }

  /** Whether a row is clickable or not */
  function getIsRowClickable(entity: T): boolean {
    return (
      (!!onRowClick || !!rowDetails) &&
      (!isRowClickable || isRowClickable(entity))
    );
  }

  function getPinnedColumns(): GridPinnedColumnFields | undefined {
    const leftColumns =
      pinnedLeftColumns && pinnedLeftColumns.length > 0
        ? [...pinnedLeftColumns]
        : [firstColumnField].filter((field) => field !== undefined);

    // The checkboxes stay next to the column identifying the row they select
    if (isRowSelectionEnabled) {
      leftColumns.unshift(GRID_CHECKBOX_SELECTION_COL_DEF.field);
    }

    return leftColumns.length > 0 ? { left: leftColumns } : undefined;
  }

  /** Ids of the rows that are still shown and selected in the model */
  function getSelectedRowIds({ ids }: GridRowSelectionModel): string[] {
    return rows.map(({ id }) => String(id)).filter((id) => ids.has(id));
  }

  return (
    <AdmDataGridSlotPropsProvider
      value={{
        toolbar: {
          onExport:
            onExport && entities.length > 0
              ? () => onExport(entities)
              : undefined,
          additionalToolbarButtons,
          leadingToolbarButtons,
          setActiveFilters,
          activeFilters,
          filterGroups,
          tableType,
        },
        noRowsOverlay: { shouldShowErrorMessage, errorMessage },
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        pinnedColumns={getPinnedColumns()}
        showCellVerticalBorder={shouldShowVerticalGridLines}
        showColumnVerticalBorder={shouldShowVerticalGridLines}
        disableColumnMenu
        disableColumnFilter
        disableRowSelectionOnClick
        checkboxSelection={isRowSelectionEnabled}
        // Selecting all rows always lists them, instead of selecting "all rows except these"
        disableRowSelectionExcludeModel
        rowSelectionModel={
          isRowSelectionEnabled
            ? { type: "include", ids: new Set(selectedRowIds) }
            : undefined
        }
        onRowSelectionModelChange={(model) =>
          onSelectedRowIdsChange?.(getSelectedRowIds(model))
        }
        showToolbar={true}
        getDetailPanelContent={rowDetails}
        getDetailPanelHeight={() => "auto"}
        getRowHeight={() => "auto"}
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
        sortingOrder={shouldForceSort ? ["asc", "desc"] : ["asc", "desc", null]}
        onRowClick={({ row, id }) => {
          if (!getIsRowClickable(row.entity)) {
            return;
          }

          if (onRowClick) {
            onRowClick(String(id));
            trackEvent({
              name: GeneralTableEvents.clickRow,
              props: {
                location: tableType,
              },
            });
            return;
          }

          if (rowDetails) {
            trackEvent({
              name: GeneralTableEvents.toggleRow,
              props: {
                location: tableType,
                action: expandedRowIds.has(id) ? "collapse" : "expand",
              },
            });
            toggleRowExpansion(id);
          }
        }}
        getRowClassName={({ row }) =>
          getIsRowClickable(row.entity) ? "row-clickable" : ""
        }
        sx={{
          // The grid sits on the page, so it takes the page colour, which its pinned cells and
          // their hover and selected tints are mixed from, instead of the paper colour
          /* eslint-disable @typescript-eslint/naming-convention -- CSS variable names */
          "--DataGrid-t-color-background-base": (theme) =>
            theme.vars.palette.background.default,
          "--DataGrid-t-header-background-base": (theme) =>
            theme.vars.palette.background.default,
          "--DataGrid-t-cell-background-pinned": (theme) =>
            theme.vars.palette.background.default,
          /* eslint-enable @typescript-eslint/naming-convention */
          // Keep pinned areas above the scrollable section to avoid content bleed-through.
          "& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns, & .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-cell--pinnedLeft":
            { zIndex: 3 },
          // Rows grow with their content, so the cells have to centre it themselves, and the
          // vertical padding is what sets how tall a row is
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            py: 1,
            px: 1.25,
          },
          "& .MuiDataGrid-row": {
            cursor: "default",
          },
          "& .MuiDataGrid-row.row-clickable": {
            cursor: "pointer",
          },
          ...hoveredColumnStyles,
          ...(shouldAllowColumnResize
            ? {
                "& .MuiDataGrid-columnSeparator--resizable": {
                  display: "flex",
                },
              }
            : {}),
          ...(shouldShowVerticalGridLines
            ? {
                "& .MuiDataGrid-withBorderColor": {
                  borderColor: "divider",
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
                  borderRight: 1,
                  borderRightStyle: "solid",
                  borderRightColor: "divider",
                },
                "& .MuiDataGrid-columnHeader:last-of-type, & .MuiDataGrid-cell:last-of-type":
                  {
                    borderRight: "none",
                  },
              }
            : {
                // The border MUI X draws after the pinned columns is not wanted without grid lines
                "& .MuiDataGrid-columnHeader--withRightBorder, & .MuiDataGrid-cell--withRightBorder":
                  {
                    borderRight: "none",
                  },
              }),
        }}
        slots={admDataGridSlots}
        initialState={{
          sorting: {
            sortModel: defaultSortModel,
          },
        }}
        filterModel={activeFilters}
        onSortModelChange={(event) => {
          trackEvent({
            name: GeneralTableEvents.changeSorting,
            props: {
              location: tableType,
              sortingBy: event[0]?.field ?? "remove previous sorting",
              sortingOrder: event[0]?.sort ?? "no sorting",
            },
          });
        }}
        onFilterModelChange={(model) => setActiveFilters(model)}
      />
    </AdmDataGridSlotPropsProvider>
  );
}
